import {
    deleteWhDemandOverrides,
    getWhDemandTable,
    upsertWhDemandOverrides,
    type WhDemandRow,
    type WhDemandTableResponse,
} from '@api/distribution';
import DataSheet, { dataSheetTokens, type DataSheetColumn } from '@components/ui/DataSheet';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { formatCount } from '@utils/currency';

const tokens = dataSheetTokens;

interface Props {
    open: boolean;
    onClose: () => void;
    channelKey: string;
    warehouses: Array<{ warehouseId: string; name: string }>;
    initialWarehouseId?: string | null;
}

type EditMap = Record<string, Record<string, number | null>>;

function fmt(n: number): string {
    if (n === 0) return '0';
    if (n < 10) return n.toFixed(1);
    return formatCount(Math.round(n));
}

function toDateString(d: Date): string {
    return d.toISOString().slice(0, 10);
}

function defaultStartDate(): string {
    const d = new Date();
    d.setDate(1);
    return toDateString(d);
}

function defaultEndDate(): string {
    const d = new Date();
    d.setMonth(d.getMonth() + 5);
    d.setDate(1);
    d.setDate(0);
    return toDateString(d);
}

function triggerCsvDownload(csv: string, filename: string) {
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
}

export function WHDemandOverrideModal({ open, onClose, channelKey, warehouses, initialWarehouseId }: Props) {
    const [selectedWarehouse, setSelectedWarehouse] = useState(initialWarehouseId ?? warehouses[0]?.warehouseId ?? '');
    const [startDate, setStartDate] = useState(defaultStartDate);
    const [endDate, setEndDate] = useState(defaultEndDate);
    const [tableData, setTableData] = useState<WhDemandTableResponse | null>(null);
    const [editMap, setEditMap] = useState<EditMap>({});
    const [search, setSearch] = useState('');
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [uploadMsg, setUploadMsg] = useState<{ type: 'ok' | 'err'; text: string } | null>(null);
    const [error, setError] = useState<string | null>(null);

    const fileInputRef = useRef<HTMLInputElement>(null);

    // Auto-select first warehouse when warehouses prop arrives async
    useEffect(() => {
        if (!selectedWarehouse && warehouses.length > 0) {
            setSelectedWarehouse(initialWarehouseId ?? warehouses[0].warehouseId);
        }
    }, [warehouses, initialWarehouseId]); // eslint-disable-line react-hooks/exhaustive-deps

    // Auto-load when modal opens
    useEffect(() => {
        if (open && selectedWarehouse) {
            fetchTable();
        }
    }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

    const fetchTable = useCallback((whIdOverride?: string) => {
        const whId = whIdOverride ?? selectedWarehouse;
        if (!whId) return;
        setLoading(true);
        setError(null);
        setTableData(null);
        setEditMap({});
        getWhDemandTable({
            channelId: channelKey,
            warehouseId: whId,
            startDate,
            endDate,
        })
            .then(data => setTableData(data))
            .catch(() => setError('Failed to load demand table'))
            .finally(() => setLoading(false));
    }, [channelKey, selectedWarehouse, startDate, endDate]);

    const setCell = useCallback((productId: string, periodKey: string, value: number | null) => {
        setEditMap(prev => ({
            ...prev,
            [productId]: { ...(prev[productId] ?? {}), [periodKey]: value },
        }));
    }, []);

    const columns = useMemo((): DataSheetColumn<WhDemandRow>[] => {
        if (!tableData) return [];

        const base: DataSheetColumn<WhDemandRow>[] = [
            {
                key: 'sku',
                header: 'SKU Code',
                sticky: true,
                width: 90,
                csvValue: (_: unknown, row: WhDemandRow) => row.sku,
                render: (_, row) => (
                    <span style={{ fontSize: 11, fontFamily: 'monospace', color: tokens.colors.textMuted }}>{row.sku}</span>
                ),
            },
            {
                key: 'name',
                header: 'Name',
                sticky: true,
                width: 180,
                csvValue: (_: unknown, row: WhDemandRow) => row.name,
                render: (_, row) => (
                    <span style={{ fontSize: 12, fontWeight: 500, color: tokens.colors.textPrimary }}>{row.name}</span>
                ),
            },
        ];

        for (const period of tableData.periods) {
            const pk = period.periodStart;
            base.push({
                key: pk,
                header: period.label,
                width: 110,
                csvValue: (_: unknown, row: WhDemandRow) => {
                    const ov = editMap[row.productId]?.[pk] ?? row.overrideValues[pk];
                    return ov != null ? ov : (row.calculatedValues[pk] ?? 0);
                },
                render: (_: unknown, row: WhDemandRow) => {
                    const calculated = row.calculatedValues[pk] ?? 0;
                    const originalOverride = row.overrideValues[pk];
                    const hasEdit = row.productId in editMap && pk in (editMap[row.productId] ?? {});
                    const currentVal = hasEdit ? editMap[row.productId][pk] : originalOverride;
                    const inputVal = currentVal == null ? '' : String(currentVal);
                    const isOverridden = currentVal != null;

                    return (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            <span style={{ fontSize: 10, color: tokens.colors.textMuted }}>
                                {fmt(calculated)}
                            </span>
                            <input
                                type="number"
                                min={0}
                                step="any"
                                value={inputVal}
                                placeholder={fmt(calculated)}
                                onChange={e => {
                                    const raw = e.target.value;
                                    setCell(row.productId, pk, raw === '' ? null : parseFloat(raw));
                                }}
                                style={{
                                    width: '100%',
                                    height: 24,
                                    padding: '0 4px',
                                    fontSize: 11,
                                    border: `1px solid ${isOverridden ? 'rgb(99, 102, 241)' : 'rgb(226, 232, 240)'}`,
                                    borderRadius: 4,
                                    outline: 'none',
                                    background: isOverridden ? 'rgb(238, 242, 255)' : 'white',
                                }}
                            />
                        </div>
                    );
                },
            });
        }

        return base;
    }, [tableData, editMap, setCell]);

    const filteredRows = useMemo<WhDemandRow[]>(() => {
        if (!tableData) return [];
        const q = search.trim().toLowerCase();
        if (!q) return tableData.rows;
        return tableData.rows.filter(r =>
            r.name.toLowerCase().includes(q) || r.sku.toLowerCase().includes(q),
        );
    }, [tableData, search]);

    // ── Inline save (per-WH edits) ──────────────────────────────────────────────
    const handleSave = async () => {
        if (!tableData) return;
        setSaving(true);
        try {
            const upsertRows = [];
            const toDelete: Array<{ productId: string; periodStart: string }> = [];

            for (const [productId, periods] of Object.entries(editMap)) {
                for (const [periodKey, value] of Object.entries(periods)) {
                    if (value != null && value >= 0) {
                        const periodMeta = tableData.periods.find(p => p.periodStart === periodKey);
                        if (periodMeta) {
                            upsertRows.push({
                                warehouseId: selectedWarehouse,
                                productId,
                                periodStart: periodKey,
                                periodType: periodMeta.periodType,
                                value,
                            });
                        }
                    } else {
                        const hadOriginal = tableData.rows.find(r => r.productId === productId)?.overrideValues[periodKey] != null;
                        if (hadOriginal) toDelete.push({ productId, periodStart: periodKey });
                    }
                }
            }

            await Promise.all([
                upsertRows.length > 0
                    ? upsertWhDemandOverrides({ channelId: channelKey, rows: upsertRows })
                    : Promise.resolve(),
                ...toDelete.map(({ productId, periodStart }) =>
                    deleteWhDemandOverrides({ channelId: channelKey, warehouseId: selectedWarehouse, productId, periodStart }),
                ),
            ]);

            fetchTable();
        } catch {
            setError('Failed to save overrides');
        } finally {
            setSaving(false);
        }
    };

    const handleClearAll = async () => {
        if (!selectedWarehouse) return;
        setSaving(true);
        try {
            await deleteWhDemandOverrides({ channelId: channelKey, warehouseId: selectedWarehouse });
            fetchTable();
        } catch {
            setError('Failed to clear overrides');
        } finally {
            setSaving(false);
        }
    };

    // ── Bulk CSV template download ───────────────────────────────────────────────
    const handleTemplateDownload = () => {
        const periodLabels = tableData?.periods.map(p => p.label) ?? (() => {
            const labels: string[] = [];
            const d = new Date();
            d.setDate(1);
            for (let i = 0; i < 6; i++) {
                const m = new Date(d.getFullYear(), d.getMonth() + i, 1);
                const mon = m.toLocaleDateString('en-US', { month: 'short' });
                const yr = m.getFullYear().toString().slice(2);
                labels.push(`${mon}-${yr}`);
            }
            return labels;
        })();

        const header = ['store_id', 'SKU Code', ...periodLabels];
        const csv = header.join(',');
        triggerCsvDownload(csv, `wh_demand_template_${channelKey}.csv`);
    };

    // ── Bulk CSV upload — direct write, multi-WH ─────────────────────────────────
    const handleCsvUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;
        if (fileInputRef.current) fileInputRef.current.value = '';

        if (!tableData) {
            setUploadMsg({ type: 'err', text: 'Load a store first so SKU codes can be resolved.' });
            return;
        }

        setUploadMsg(null);

        const text = await file.text();
        const lines = text.trim().split('\n');
        if (lines.length < 2) return;

        const headerCells = lines[0].split(',').map(c => c.trim());
        const labelToKey: Record<string, string> = {};
        const labelToPeriodType: Record<string, string> = {};
        for (const p of tableData.periods) {
            labelToKey[p.label] = p.periodStart;
            labelToPeriodType[p.label] = p.periodType;
        }

        const skuToProductId: Record<string, string> = {};
        for (const row of tableData.rows) skuToProductId[row.sku] = row.productId;

        const rows: Array<{ warehouseId: string; productId: string; periodStart: string; periodType: string; value: number }> = [];
        let skipped = 0;

        for (let i = 1; i < lines.length; i++) {
            const line = lines[i].trim();
            if (!line) continue;
            const cells = line.split(',').map(c => c.trim());
            const storeId = cells[0];
            const sku = cells[1];
            const productId = skuToProductId[sku];
            if (!storeId || !productId) { skipped++; continue; }

            for (let j = 2; j < headerCells.length; j++) {
                const label = headerCells[j];
                const periodKey = labelToKey[label];
                const periodType = labelToPeriodType[label];
                if (!periodKey || !periodType) continue;
                const raw = cells[j];
                if (!raw || raw === '') continue;
                const value = parseFloat(raw);
                if (isNaN(value) || value < 0) continue;
                rows.push({ warehouseId: storeId, productId, periodStart: periodKey, periodType, value });
            }
        }

        if (rows.length === 0) {
            setUploadMsg({ type: 'err', text: `No valid rows found.${skipped > 0 ? ` ${skipped} rows had unrecognised SKUs.` : ''}` });
            return;
        }

        setSaving(true);
        // Upstream proxy caps body at ~1 MB (nginx default), tighter than express's 2 MB.
        // Rows are ~116 B each, so 2.5k/chunk ~= 290 KB — safe even if the cap is 512 KB.
        const CHUNK_SIZE = 2_500;
        try {
            for (let i = 0; i < rows.length; i += CHUNK_SIZE) {
                await upsertWhDemandOverrides({ channelId: channelKey, rows: rows.slice(i, i + CHUNK_SIZE) });
            }
            setUploadMsg({ type: 'ok', text: `${rows.length} override${rows.length !== 1 ? 's' : ''} saved.${skipped > 0 ? ` ${skipped} rows skipped (unknown SKU).` : ''}` });
            fetchTable();
        } catch {
            setUploadMsg({ type: 'err', text: 'Upload failed — please try again.' });
        } finally {
            setSaving(false);
        }
    };

    const hasEdits = Object.keys(editMap).length > 0;
    const selectedWHName = warehouses.find(w => w.warehouseId === selectedWarehouse)?.name ?? selectedWarehouse;

    if (!open) return null;

    return createPortal(
        <div
            style={{
                position: 'fixed', inset: 0, zIndex: 9999,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(2px)', padding: 16,
            }}
        >
            <div
                style={{
                    width: '92vw', maxWidth: 1400, height: '88vh',
                    background: 'white', borderRadius: 16,
                    boxShadow: '0 25px 50px rgba(0,0,0,0.25)',
                    display: 'flex', flexDirection: 'column', overflow: 'hidden',
                }}
            >
                {/* Header */}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 24px', borderBottom: '1px solid rgb(226,232,240)', flexShrink: 0 }}>
                    <div>
                        <h2 style={{ margin: 0, fontSize: 17, fontWeight: 700, color: tokens.colors.textPrimary }}>
                            WH Demand Overrides — {channelKey}
                        </h2>
                        <p style={{ margin: '2px 0 0', fontSize: 12, color: tokens.colors.textMuted }}>
                            Override calculated DRR-based demand per SKU × period. Inline edit per store, or bulk-upload across all stores via CSV.
                        </p>
                    </div>
                    <button onClick={onClose} style={{ width: 32, height: 32, borderRadius: 8, border: '1px solid rgb(226,232,240)', background: 'white', cursor: 'pointer', fontSize: 18, color: tokens.colors.textMuted }}>
                        ×
                    </button>
                </div>

                {/* Store filter + Load */}
                <div style={{ display: 'flex', alignItems: 'flex-end', gap: 12, padding: '12px 24px', borderBottom: '1px solid rgb(226,232,240)', flexShrink: 0, flexWrap: 'wrap' }}>
                    <FilterField label="Store / Warehouse">
                        <select
                            value={selectedWarehouse}
                            onChange={e => {
                                const newWh = e.target.value;
                                setSelectedWarehouse(newWh);
                                fetchTable(newWh);
                            }}
                            style={selectStyle}
                        >
                            {warehouses.map(w => <option key={w.warehouseId} value={w.warehouseId}>{w.name}</option>)}
                        </select>
                    </FilterField>

                    <FilterField label="Start date">
                        <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} style={inputStyle} />
                    </FilterField>

                    <FilterField label="End date">
                        <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} style={inputStyle} />
                    </FilterField>

                    <button
                        onClick={() => fetchTable()}
                        disabled={!selectedWarehouse || loading}
                        style={{ ...primaryBtnStyle, opacity: (!selectedWarehouse || loading) ? 0.5 : 1, cursor: (!selectedWarehouse || loading) ? 'not-allowed' : 'pointer' }}
                    >
                        {loading ? 'Loading…' : 'Load'}
                    </button>

                    {/* Bulk CSV — lives in the filter bar, not per-WH */}
                    <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 8 }}>
                        {uploadMsg && (
                            <span style={{ fontSize: 11, color: uploadMsg.type === 'ok' ? 'rgb(22, 163, 74)' : 'rgb(220, 38, 38)' }}>
                                {uploadMsg.text}
                            </span>
                        )}
                        <input ref={fileInputRef} type="file" accept=".csv" style={{ display: 'none' }} onChange={handleCsvUpload} />
                        <button onClick={handleTemplateDownload} style={ghostBtnStyle}>Download Template</button>
                        <button
                            onClick={() => fileInputRef.current?.click()}
                            disabled={saving}
                            style={{ ...ghostBtnStyle, opacity: saving ? 0.5 : 1 }}
                        >
                            {saving ? 'Uploading…' : 'Upload CSV'}
                        </button>
                    </div>
                </div>

                {/* Body */}
                <div style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
                    {error && (
                        <div style={{ padding: '8px 24px', color: 'rgb(220, 38, 38)', fontSize: 12 }}>{error}</div>
                    )}

                    {tableData && (
                        <DataSheet<WhDemandRow>
                            data={filteredRows}
                            columns={columns}
                            title={`${selectedWHName} — Demand by SKU`}
                            subtitle={`${tableData.periods.length} periods · ${filteredRows.length} SKUs · Grey = calculated; blue border = override active`}
                            enableSearch
                            searchValue={search}
                            onSearchChange={setSearch}
                            searchPlaceholder="Search SKU or name…"
                            enableDownload
                            downloadFilename={`wh_demand_${selectedWHName.replace(/\s+/g, '_')}_${tableData.periodType?.toLowerCase() ?? 'monthly'}.csv`}
                            toolbarControls={
                                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                                    <button
                                        onClick={handleClearAll}
                                        disabled={saving}
                                        style={{ ...ghostBtnStyle, color: 'rgb(220, 38, 38)', borderColor: 'rgb(252, 165, 165)' }}
                                    >
                                        Clear All
                                    </button>
                                    <button
                                        onClick={handleSave}
                                        disabled={saving || !hasEdits}
                                        style={{ ...primaryBtnStyle, opacity: hasEdits ? 1 : 0.5 }}
                                    >
                                        {saving ? 'Saving…' : 'Save Overrides'}
                                    </button>
                                </div>
                            }
                        />
                    )}

                    {!tableData && !loading && !error && (
                        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: tokens.colors.textMuted, fontSize: 14 }}>
                            {selectedWarehouse ? 'Click Load to view demand data.' : 'No stores configured for this channel.'}
                        </div>
                    )}

                    {loading && (
                        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: tokens.colors.textMuted, fontSize: 14 }}>
                            Loading demand table…
                        </div>
                    )}
                </div>
            </div>
        </div>,
        document.body,
    );
}

// ─── Style helpers ─────────────────────────────────────────────────────────────

const FilterField: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        <span style={{ fontSize: 11, fontWeight: 500, color: 'rgb(100, 116, 139)' }}>{label}</span>
        {children}
    </div>
);

const baseInput: React.CSSProperties = {
    height: 32, padding: '0 8px', fontSize: 12,
    border: '1px solid rgb(226, 232, 240)', borderRadius: 6,
    outline: 'none', background: 'white', color: 'rgb(26, 32, 44)',
};
const selectStyle: React.CSSProperties = { ...baseInput, minWidth: 150 };
const inputStyle: React.CSSProperties = { ...baseInput, width: 130 };
const primaryBtnStyle: React.CSSProperties = {
    height: 32, padding: '0 14px', fontSize: 12, fontWeight: 600,
    color: 'white', background: 'rgb(99, 102, 241)', border: 'none', borderRadius: 6, cursor: 'pointer',
};
const ghostBtnStyle: React.CSSProperties = {
    height: 32, padding: '0 12px', fontSize: 12, fontWeight: 500,
    color: 'rgb(15, 23, 42)', background: 'white', border: '1px solid rgb(226, 232, 240)', borderRadius: 6, cursor: 'pointer',
};
